/**
 * reactionModel — the pure half of emoji reactions (PR-4, 2026-09-09).
 *
 * Store: `group_thread_messages/{threadId}/reactions/{messageId}` —
 *   { threadId, messageId, orderTs, byUser: { [uid]: string[] }, updatedAt }
 * One doc per message, one list per reacting user. The doc id is the message's
 * SHARED id (the key every participant's browser agrees on):
 *   • the assistant's bubble  → its task anchor `prest0n_task_<taskId>`
 *   • the viewer's own bubble → `sharedMessageId` (`prest0n_task_<taskId>__user`, PR-2)
 *   • another participant's  → the store doc id it arrived under
 * Greetings, system rows, pending/streaming turns and anything without a
 * shared id are not reactable. In a GROUP thread a message is reactable only
 * once the shared store carries it (the rules require the message doc to
 * exist with the same orderTs) — the store's orderTs for each id is learned
 * from the overlay listener; a SOLO thread has no message docs, so the rules
 * accept the bubble's own instant.
 */
import type { ChatMessage } from '../../components/GlobalChatSystem/types';
import { deriveTaskAnchorId } from '../../types/taskAnchor';
import { MAX_REACTIONS_PER_USER, isAllowedReactionEmoji } from '../../constants/reactionEmoji';

export type ReactionsByUser = Record<string, string[]>;

export interface ReactionDoc {
  threadId: string;
  messageId: string;
  orderTs: number;
  byUser: ReactionsByUser;
}

/** Reactions of the active thread, keyed by the message's shared id. */
export type ReactionsByMessage = Record<string, ReactionDoc>;

type MessageLike = Pick<ChatMessage, 'id' | 'isUser' | 'isSystem' | 'sourceTag' | 'status' | 'senderId' | 'sharedMessageId' | 'taskAnchorId' | 'serverTaskId' | 'orderTs' | 'timestamp'>;

/** The shared id a reaction to this message is keyed on; null = not reactable. */
export function reactionTargetId(m: MessageLike | null | undefined, selfUid: string | null | undefined): string | null {
  if (!m || m.isSystem) return null;
  if (m.sourceTag === 'welcome') return null; // the seeded greeting is chrome
  if (m.status === 'streaming') return null; // not delivered yet
  const self = (selfUid ?? '').trim();
  if (m.isUser !== true) {
    const anchor = (typeof m.taskAnchorId === 'string' && m.taskAnchorId.trim())
      || deriveTaskAnchorId(m.serverTaskId ?? null)
      || '';
    return anchor || null;
  }
  const sender = typeof m.senderId === 'string' ? m.senderId.trim() : '';
  if (!sender || sender === self) {
    const shared = typeof m.sharedMessageId === 'string' ? m.sharedMessageId.trim() : '';
    return shared || null;
  }
  const id = typeof m.id === 'string' ? m.id.trim() : '';
  return id || null;
}

/**
 * In a SOLO thread every real turn can be keyed on its own local id: the owner
 * is the only reader, and the per-doc store keeps that id on every device. Own
 * turns sent before the shared id was minted client-side (PR-2) and answers
 * without a task anchor were otherwise never reactable (2026-09-09).
 */
export function soloFallbackReactionId(m: MessageLike | null | undefined): string | null {
  if (!m || m.isSystem) return null;
  if (m.sourceTag === 'welcome') return null;
  if (m.status === 'streaming') return null;
  const id = typeof m.id === 'string' ? m.id.trim() : '';
  return id || null;
}

/** The message's own instant (solo threads; the fallback when the store is not the source). */
export function localOrderTs(m: MessageLike | null | undefined): number | null {
  if (!m) return null;
  if (typeof m.orderTs === 'number' && Number.isFinite(m.orderTs)) return Math.floor(m.orderTs);
  const t = m.timestamp as unknown;
  const ms = t instanceof Date ? t.getTime() : new Date(t as string).getTime();
  return Number.isFinite(ms) && ms > 0 ? Math.floor(ms) : null;
}

export interface ReactabilityContext {
  selfUid: string | null | undefined;
  /** True when the thread has other human participants (message docs exist in the store). */
  isGroup: boolean;
  /** shared id → the store's orderTs, learned from the overlay listener (group threads). */
  sharedOrderTs: Readonly<Record<string, number>>;
}

/**
 * Where a reaction to `m` would be written, and with which instant — or null
 * when the message cannot take one yet (the rules would refuse it anyway).
 */
export function reactionTarget(
  m: MessageLike | null | undefined,
  ctx: ReactabilityContext,
): { messageId: string; orderTs: number } | null {
  const messageId = reactionTargetId(m, ctx.selfUid) ?? (ctx.isGroup ? null : soloFallbackReactionId(m));
  if (!messageId) return null;
  if (ctx.isGroup) {
    const stored = ctx.sharedOrderTs[messageId];
    if (typeof stored !== 'number' || !Number.isFinite(stored) || stored < 0) return null; // not delivered to the store yet
    return { messageId, orderTs: Math.floor(stored) };
  }
  const own = localOrderTs(m);
  return own === null ? null : { messageId, orderTs: own };
}

/** Pure toggle: add when absent, remove when present; caps the list; ignores unknown emoji. */
export function toggleInList(list: ReadonlyArray<string> | undefined, emoji: string): { next: string[]; added: boolean } | null {
  if (!isAllowedReactionEmoji(emoji)) return null;
  const current = Array.isArray(list) ? list.filter(isAllowedReactionEmoji) : [];
  if (current.includes(emoji)) return { next: current.filter((e) => e !== emoji), added: false };
  if (current.length >= MAX_REACTIONS_PER_USER) return null;
  return { next: [...current, emoji], added: true };
}

export interface ReactionSummaryEntry {
  emoji: string;
  count: number;
  uids: string[];
  /** The viewer is among them. */
  mine: boolean;
}

/** Chips in first-seen order: the emoji, how many, who — and whether the viewer is one of them. */
export function summarizeReactions(byUser: ReactionsByUser | null | undefined, selfUid: string | null | undefined): ReactionSummaryEntry[] {
  const self = (selfUid ?? '').trim();
  const order: string[] = [];
  const byEmoji = new Map<string, string[]>();
  for (const [uid, list] of Object.entries(byUser ?? {})) {
    if (!Array.isArray(list)) continue;
    for (const emoji of list) {
      if (!isAllowedReactionEmoji(emoji)) continue;
      if (!byEmoji.has(emoji)) {
        byEmoji.set(emoji, []);
        order.push(emoji);
      }
      const uids = byEmoji.get(emoji)!;
      if (!uids.includes(uid)) uids.push(uid);
    }
  }
  return order.map((emoji) => {
    const uids = byEmoji.get(emoji)!;
    return { emoji, count: uids.length, uids, mine: !!self && uids.includes(self) };
  });
}

/** Parse one store doc defensively; null when it is not a reaction doc. */
export function parseReactionDoc(id: string, raw: unknown): ReactionDoc | null {
  const d = (raw && typeof raw === 'object' ? raw : null) as Record<string, unknown> | null;
  if (!d) return null;
  const messageId = (typeof d.messageId === 'string' && d.messageId.trim()) || (id ?? '').trim();
  const orderTs = typeof d.orderTs === 'number' && Number.isFinite(d.orderTs) ? Math.floor(d.orderTs) : null;
  if (!messageId || orderTs === null) return null;
  const byUser: ReactionsByUser = {};
  const rawBy = d.byUser && typeof d.byUser === 'object' ? (d.byUser as Record<string, unknown>) : {};
  for (const [uid, list] of Object.entries(rawBy)) {
    const key = (uid ?? '').trim();
    if (!key || !Array.isArray(list)) continue;
    const clean = list.filter(isAllowedReactionEmoji).slice(0, MAX_REACTIONS_PER_USER);
    if (clean.length) byUser[key] = clean;
  }
  return { threadId: typeof d.threadId === 'string' ? d.threadId : '', messageId, orderTs, byUser };
}
